
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Form, Alert } from "react-bootstrap";
import { IconCaretLeft } from "@tabler/icons-react";
import { Helmet } from "react-helmet-async";

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);

  // for duplicate name validation
  const [existingNames, setExistingNames] = useState(new Set());
  const [nameConflict, setNameConflict] = useState(false);

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // revalidate conflict whenever projectName or existingNames change
    const name = (projectName || "").trim().toLowerCase();
    // exclude current project's own name from conflict check
    const currentName = (project?.name || "").trim().toLowerCase();
    setNameConflict(name.length > 0 && name !== currentName && existingNames.has(name));
  }, [projectName, existingNames, project]);

  const fetchProject = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const projectResponse = await fetch(`/api/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!projectResponse.ok) {
        if (projectResponse.status === 403 || projectResponse.status === 404) {
          navigate("/404");
          return;
        }
        throw new Error("Failed to load project.");
      }
      const projectData = await projectResponse.json();
      setProject(projectData);
      setProjectName(projectData.name || "");
      setProjectDescription(projectData.description || "");

      // fetch all user project names for duplicate validation
      await fetchAllProjectNames(token, projectData.id);
    } catch (err) {
      setError(err.message || "Unexpected error.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProjectNames = async (token, currentProjectId) => {
    try {
      // request a large page to cover all user's projects (server-side pagination compatible)
      const resp = await fetch(`/api/projects?limit=10000&page=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        // non-fatal for UI; just skip duplicate validation if it fails
        return;
      }
      const data = await resp.json();
      const names = new Set(
        (data.projects || [])
          .filter((p) => p.id !== currentProjectId)
          .map((p) => (p.name || "").trim().toLowerCase())
          .filter((n) => n.length > 0)
      );
      setExistingNames(names);
    } catch (_) {
      // ignore
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    const trimmedName = (projectName || "").trim();
    if (!trimmedName) {
      setError("Please enter the project name.");
      return;
    }

    if (nameConflict) {
      setError("A project with this name already exists. Please choose a different name.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/projects/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: trimmedName, description: projectDescription }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        // if backend also returns duplicate error or validation error, surface it
        throw new Error(errText || "Failed to update project.");
      }

      setSuccessMessage("Project updated successfully!");
      setTimeout(() => {
        navigate(`/project/${id}`);
      }, 1200);
    } catch (err) {
      setError(`Error updating project: ${err.message}`);
    }
  };

  return (
    <>
      <Helmet>
    <title>
      {projectName ? `Edit ${projectName} - IndexBooster` : "Edit Project - IndexBooster"}
    </title>
      </Helmet>

      {loading && <p>Loading...</p>}
      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {project && (
        <Card>
          <Card.Header className="d-flex align-items-center gap-2">
            <Button variant="light" size="sm" onClick={() => navigate(-1)}>
              <IconCaretLeft size={18} />
            </Button>
            <h3 className="card-title mb-0">Edit Project</h3>
          </Card.Header>

          <Card.Body>
            <Form onSubmit={handleEditProject}>
              <Form.Group className="mb-3">
                <Form.Label>Project Name</Form.Label>
                <Form.Control
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  isInvalid={nameConflict || (!projectName || projectName.trim().length === 0)}
                  required
                />
                {nameConflict && (
                  <Form.Control.Feedback type="invalid">
                    A project with this name already exists.
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  rows={3}
                />
              </Form.Group>

              <div className="d-flex justify-content-between">
                <Button variant="primary" type="submit" disabled={nameConflict}>
                  Save Changes
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default EditProject;
